import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Ellipsis, LogOut } from 'lucide-react'
import { cn } from '@/lib/utils'
import { BuildStamp } from './BuildStamp'
import { Drawer } from './Drawer'
import { activeNav, NAV, navPathname, TAB_PATHS } from './nav'
import { useLogout } from './session'
import { NavLinkItem, useNavBadges } from './Sidebar'
import { TONE_CLASS } from '@/lib/status'
import { ThemeSwitch } from './ThemeSwitch'

/** The phone's navigation (below 768px): 오늘 · 달력 · 앱 · 국가, and 더보기
 *  for the other five, the theme and logging out. */
export function BottomTabs({ className }: { className?: string }) {
  const active = activeNav(navPathname(useLocation()))
  const [more, setMore] = useState(false)
  const badges = useNavBadges()
  const logout = useLogout()
  const tabs = TAB_PATHS.map((p) => NAV.find((i) => i.to === p)!)
  const rest = NAV.filter((i) => !TAB_PATHS.includes(i.to))
  const inRest = !!active && !TAB_PATHS.includes(active.to)

  return (
    <>
      <nav
        aria-label="주 메뉴"
        className={cn(
          'fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card/95 pb-[env(safe-area-inset-bottom)] backdrop-blur',
          className,
        )}
      >
        <ul className="grid grid-cols-5">
          {tabs.map((item) => {
            const on = active?.to === item.to
            const Icon = item.icon
            const badge = item.badge ? badges[item.badge] : undefined
            const n = badge?.n ?? 0
            return (
              <li key={item.to}>
                <Link
                  to={item.to}
                  aria-current={on ? 'page' : undefined}
                  className={cn(
                    'relative flex h-14 flex-col items-center justify-center gap-0.5 text-micro',
                    on ? 'font-semibold text-foreground' : 'text-muted-foreground',
                  )}
                >
                  {on && <span aria-hidden className="absolute inset-x-5 top-0 h-0.5 rounded-full bg-foreground" />}
                  <span className="relative">
                    <Icon size={20} aria-hidden />
                    {n > 0 && (
                      <span
                        className={cn(
                          'absolute -right-2.5 -top-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold tabular-nums text-card',
                          TONE_CLASS[badge!.tone].dot,
                        )}
                      >
                        {n}
                      </span>
                    )}
                  </span>
                  {item.label}
                </Link>
              </li>
            )
          })}
          <li>
            <button
              type="button"
              onClick={() => setMore(true)}
              aria-haspopup="dialog"
              aria-expanded={more}
              className={cn(
                'relative flex h-14 w-full flex-col items-center justify-center gap-0.5 text-micro',
                inRest ? 'font-semibold text-foreground' : 'text-muted-foreground',
              )}
            >
              {inRest && <span aria-hidden className="absolute inset-x-5 top-0 h-0.5 rounded-full bg-foreground" />}
              <Ellipsis size={20} aria-hidden />
              {inRest ? active!.label : '더보기'}
            </button>
          </li>
        </ul>
      </nav>

      <Drawer open={more} onClose={() => setMore(false)} side="bottom" title="메뉴">
        <ul className="grid grid-cols-1 gap-0.5">
          {rest.map((item) => (
            <li key={item.to}>
              <NavLinkItem item={item} active={active?.to === item.to} badges={badges} onNavigate={() => setMore(false)} />
            </li>
          ))}
        </ul>
        <div className="mt-4 space-y-3 border-t border-border pt-4">
          <ThemeSwitch />
          <div className="flex items-center justify-between">
            <BuildStamp />
            <button
              type="button"
              onClick={() => void logout()}
              className="inline-flex h-9 items-center gap-1.5 rounded-md px-2 text-body text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <LogOut size={16} aria-hidden />
              로그아웃
            </button>
          </div>
        </div>
      </Drawer>
    </>
  )
}
